import { Component, Input, OnInit } from '@angular/core';
import {
  CheckRoomService,
  DeviceService,
  PriceService,
  ReqService,
  RoomBookingService,
  SearchStateService,
} from '@core/services';
import { MbDialog, MbIcon, MbAdvice, MbBadge, MbButton } from '@am/moebius';
import { ActivatedRoute, Router } from '@angular/router';
import { IDisneyClusterTickets } from '@core/models/modals';
import { IHotelAvailability } from '@core/models/HotelAvailability/IHotelAvailability';
import { IHotelStaticInfo } from '@core/models/HotelStaticInfo/hotelStaticInfo.interface';
import { IPrepaidRoomClusterGroup } from '@core/models/PrepaidRoomClusterGroup/IPrepaidRoomCluster';
import { AppState } from '@store/app.reducer';
import { Store } from '@ngrx/store';
import { IMealPlanCluster } from '@core/models/DisneyTickets/IMealPlan.interface';
import { take } from 'rxjs/operators';
import { IRate } from '@core/models/Rate/Rate.interface';
import { ProductType } from '@am/trips-update-service';
import { Rate } from '@core/models/Rate/Rate.model';
import { disneyTickets } from '@store/disney-tickets/disney-tickets.actions';
import {
  DISNEY_FREE_MEAL_PLAN_CODES,
} from '@shared/constants/disney-offer-codes';
import { CurrencyPipe } from '@shared/pipes/currency.pipe';
import { AmExpertsCompensationComponent } from '@am/experts-compensation';
import { AmRewardsPricingComponent } from '@am/rewards-pricing';
import { PriceComponent } from '@commons/price/price.component';
import { AmTripsAddProductModule } from '@am/trips-add-product';
import { PaymentAdviseInfoComponent } from '../../components/detail-cluster/payment-advise-info/payment-advise-info.component';
import { ComboTicketComponent } from '../combo-ticket/combo-ticket.component';
import { RoomComponent } from '../../components/room/room.component';
import { DecimalPipe } from '@angular/common';

const ROOM_ONLY = 'ROOM_ONLY';

@Component({
    selector: 'disney-cluster',
    templateUrl: './disney-cluster.component.html',
    styleUrls: ['./disney-cluster.component.scss'],
    standalone: true,
    imports: [
        MbIcon,
        RoomComponent,
        ComboTicketComponent,
        MbAdvice,
        MbBadge,
        PaymentAdviseInfoComponent,
        AmTripsAddProductModule,
        PriceComponent,
        AmRewardsPricingComponent,
        MbButton,
        AmExpertsCompensationComponent,
        DecimalPipe,
        CurrencyPipe,
    ],
})
export class DisneyClusterDisneyComponent implements OnInit {
  @Input()
  hotelAvailabilityInfo: IHotelAvailability;
  @Input()
  cluster: IPrepaidRoomClusterGroup;
  @Input()
  clusterType: any;
  @Input()
  index: any;
  @Input()
  priceByValue: string;
  @Input()
  hotelStatic: IHotelStaticInfo;
  @Input()
  showAmountTotal: boolean;
  @Input() isExperts = false;

  public mealPlans: IMealPlanCluster[];
  public selectedPlan: any;
  public defaultRate: any;
  public loading: boolean;
  public isAvailable: boolean;
  public displayTooltip: boolean;
  public quantityNights: number;
  public quantityGuests: number;
  public quantityRooms: number;
  public selectedRate: any;
  public preSelectedRate: any;
  public hasSameTicketForPlan: any;
  public isAlliance: boolean;
  public samePlanWithPromo: any;
  public pricePerNight: any;
  public pricePerNightWithoutDiscount: any;
  public priceTotal: any;
  public commission: any;
  public mealPlanWithTickets: string[];
  public isMobile: boolean;
  public tripFavoriteData: any;
  public currencyCodeToTripFavorite: string;
  public isCurrencyUSD = false;
  public hasTickets = false;
  public hasMealPlan = false;
  public hasRoomOnlyOption = false;

  public readonly productType: ProductType = ProductType.HOTEL;

  constructor(
    private dialog: MbDialog,
    private reqService: ReqService,
    private searchStateService: SearchStateService,
    private priceService: PriceService,
    private deviceService: DeviceService,
    private routerActive: ActivatedRoute,
    private router: Router,
    private store: Store<AppState>,
    private roomBookingService: RoomBookingService,
    private checkRoomRateService: CheckRoomService,
  ) {
    this.isMobile = this.deviceService.isMobile();
  }

  ngOnInit() {
    this.isCurrencyUSD = this.searchStateService.getCurrencyCode() === 'USD';
    this.isAlliance = this.reqService.isAlliance();
    // Set cluster to false and leave component if no cluster has been passed
    if (this.cluster === undefined) {
      this.cluster = null;
      return false;
    }
    // Attributes initialization
    this.defaultRate = this.rateDefault;
    this.loading = false;
    this.displayTooltip = false;

    // Attributes initialization
    const decodedRooms = this.searchStateService.getDecodedRooms();

    this.quantityNights = this.priceService.totalNights;
    this.quantityRooms = this.priceService.totalRooms;
    this.quantityGuests = decodedRooms.total;

    this.mealPlanWithTickets = [];

    // Initialization of default rate
    this.setToDefaultRate();
    this.hasMealPlan = this.checkIfHasMealPlans();
    this.hasTickets = this.checkIfHasTickets();
    this.hasRoomOnlyOption = this.checkIfHasRoomOnlyOption();
    if (this.isExperts) {
      this.setTripFavoriteData();
    }
  }

  public superclubPrice(rate) {
    return Math.ceil(rate);
  }

  public setToDefaultRate() {
    this.isAvailable = true;
    // this.defaultRate.supplements = [];
    this.selectedRate = this.defaultRate;
    // this.preSelectedRate = this.selectedPlan.rates[1];
    // this.selectedTicket = defaultTicket ? defaultTicket.id : '';
    this.hasSameTicketForPlan = true;
    this.samePlanWithPromo = true;
    this.pricePerNight = this.priceService.getPrice(
      this.defaultRate,
      'NIGHT',
      this.showAmountTotal,
    );
    this.pricePerNightWithoutDiscount = this.priceService.getPriceWithoutDiscount(
      this.defaultRate,
      'NIGHT',
      this.showAmountTotal,
    );
    this.priceTotal = this.priceService.getPrice(
      this.defaultRate,
      'TOTAL',
      this.showAmountTotal,
    );
    this.commission =
      this.defaultRate.price.commission.value - this.defaultRate.price.commission.tax;
  }

  /**
   * Redirect to cart using the selected rate
   */
  public bookRate() {
    this.loading = true;
    if (!this.hasTickets && !this.hasMealPlan) {
      this.bookingRate(this.selectedRate);
    } else {
      this.store.dispatch(
        disneyTickets({ disneyTicketCluster: this.dataTicketsCluster }),
      );
      this.redirectToTickets();
    }
  }

  public redirectToTickets() {
    const url = 'hotels/add-ons';
    const hotel = this.routerActive.snapshot.params.query;
    const navigateParams = this.routerActive.snapshot.queryParams;
    this.router.navigate([`${url + '/' + hotel}`], { queryParams: navigateParams });
  }

  get dataTicketsCluster(): IDisneyClusterTickets {
    return {
      preSelectedRate: this.defaultRate,
      clusterType: this.clusterType,
      selectedPlan: this.selectedPlan,
      nameHotel: this.hotelAvailabilityInfo.name,
      meal_plan: this.mealPlans,
      nameRoom: this.cluster.rooms[0].parsedName,
      cluster: this.cluster,
      roomPricePerNight: this.priceService.getPrice(
        this.defaultRate,
        'NIGHT',
        this.showAmountTotal,
      ),
    };
  }

  public bookingRate(selectedRateData: IRate) {
    let hotelAvailabilityInfo;
    this.store
      .select('hotelsAvailability')
      .pipe(take(1))
      .subscribe((hotelAvailability) => {
        hotelAvailabilityInfo = hotelAvailability.hotelAvailability;
      });
    if (this.roomBookingService.loading) {
      return false;
    } else {
      if (
        selectedRateData.status === 'CHECKED_NO_CHANGE' ||
        selectedRateData.status === 'CHECKED_CHANGE'
      ) {
        this.roomBookingService
          .bookRate(selectedRateData, hotelAvailabilityInfo)
          .pipe(take(1))
          .subscribe((res: any) => {
            window.location.href =
              window.location.protocol +
              '//' +
              `${res.urlToRedirect}&currencyCode=${selectedRateData.price.currency.code}`;
          });
      } else {
        this.checkRoomRateService
          .getRoomRate(selectedRateData as Rate)
          .subscribe((res) => {
            selectedRateData['clicked'] = true;
            switch (res.status) {
              case 'CHECKED_NO_CHANGE':
                selectedRateData = res;
                this.bookingRate(selectedRateData);
                break;
              case 'CHECKED_CHANGE':
                const oldPrice = selectedRateData.price.show_amount_per_night_per_room;
                const newPrice = res.price.show_amount_per_night_per_room;
                selectedRateData['changed'] =
                  oldPrice !== newPrice && oldPrice > newPrice;
                selectedRateData.price.show_amount_per_night_per_room = newPrice;
                break;
              case 'UNAVAILABLE':
              case 'UNVAILABLE':
                res.status = 'UNAVAILABLE';
                res.unavailable = true;
                break;
            }
            selectedRateData.status = res.status;
          });
      }
    }
  }

  get hasDiningCard(): boolean {
    return this.cluster.hasDiningCard;
  }

  get diningCardTitle(): string {
    const offerCode = (this.cluster.rooms[0] as any)?.offerCode;
    if (DISNEY_FREE_MEAL_PLAN_CODES.includes(offerCode)) {
      return 'Habitación con planes de comidas gratuitos!';
    }
    return 'Habitación con Disney Dining Card gratuita!';
  }

  get isFreeMealPlan(): boolean {
    return DISNEY_FREE_MEAL_PLAN_CODES.includes(this.offerCode);
  }

  get offerCode(): string {
    return (this.cluster.rooms[0] as any)?.offerCode;
  }

  get rateDefault() {
    return this.cluster.rates[0];
  }

  /**
   * Check if has some rate with supplements of type: TICKET
   * @private
   */
  private checkIfHasTickets(): boolean {
    return this.cluster.rates.some(
      (rate) =>
        rate.supplements &&
        rate.supplements.some((supplement) => supplement.type === 'TICKET'),
    );
  }

  /**
   * Check if some rate have meal plan
   * @private
   */
  private checkIfHasMealPlans(): boolean {
    return this.cluster.rates.some((rate) => rate.meal_plan.code !== ROOM_ONLY);
  }

  /**
   * Check if some rate is ROOM_ONLY
   * @private
   */
  private checkIfHasRoomOnlyOption(): boolean {
    return this.cluster.rates.some((rate) => rate.meal_plan.code === ROOM_ONLY);
  }

  private setTripFavoriteData() {
    const query = this.searchStateService.getLocationSearch();
    const room = query.rooms;
    const dates = query.date ? query.date.split(',') : '';
    const info: any = {};

    if (this.selectedRate) {
      info.rate_id = this.selectedRate.id;
      this.currencyCodeToTripFavorite = this.selectedRate.price.currency.code;
    }

    this.tripFavoriteData = Object.assign(info, this.hotelStatic, {
      room: room,
      checkin: dates[0],
      checkout: dates[1],
    });
  }
}
